import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	buildListingURL,
	Dialog,
	EmptyState,
	LocaleCode,
	MerchantIntegration,
	Product,
	PublicationLimit,
	Spinner,
} from '@anticatrade/shared';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlCheckbox from '@shoelace-style/shoelace/dist/react/checkbox/index.js';
import { AppContext, appContext } from '../../../context/AppContext';
import { PublishJob } from './useExtension';

interface PublishDialogProps {
	product: Product | null;
	merchantIntegrations: MerchantIntegration[];
	isLoading: boolean;
	isExtensionAvailable: boolean;
	onClose: () => void;
	onPublicationsChange: (productId: number, publications: Product['publications']) => void;
	requestPublish: (job: PublishJob) => void;
}

const PublishDialog = ({
	product,
	merchantIntegrations,
	isLoading,
	isExtensionAvailable,
	onClose,
	onPublicationsChange,
	requestPublish,
}: PublishDialogProps) => {
	const [pendingMerchantIntegrationID, setPendingMerchantIntegrationID] = useState<number | null>(
		null,
	);
	const [productToPublish, setProductToPublish] = useState<Product | null>(product);

	const { t, i18n } = useTranslation();

	const { api, showStatus, handleError, redirect } = useContext(
		appContext as React.Context<AppContext>,
	);

	useEffect(() => {
		if (product != null) {
			setProductToPublish(product);
		}
	}, [product]);

	const updateProductPublications = (mi: MerchantIntegration) => {
		if (productToPublish == null) {
			return;
		}
		onPublicationsChange(productToPublish.id, [
			...(productToPublish.publications ?? []),
			{
				merchantIntegrationID: mi.id,
				integrationKey: mi.integrationKey,
				externalID: null,
			},
		]);
	};

	const publish = async (mi: MerchantIntegration) => {
		if (productToPublish == null) {
			return;
		}

		if (mi.metadata?.channel === 'marketplace') {
			// Publish via extension.
			if (!isExtensionAvailable) {
				showStatus({
					variant: 'warning',
					icon: 'puzzle',
					text: t('Install the AnticaTrade extension to publish on this marketplace'),
				});
				return;
			}
			// Enforce the integration's daily publish limit.
			setPendingMerchantIntegrationID(mi.id);
			let publicationLimit: PublicationLimit;
			try {
				const res = await api.publicationLimit(mi.id);
				publicationLimit = res.publicationLimit;
			} catch (err) {
				setPendingMerchantIntegrationID(null);
				handleError(err as Error);
				return;
			}
			setPendingMerchantIntegrationID(null);
			if (publicationLimit.limit > 0 && publicationLimit.used >= publicationLimit.limit) {
				showStatus({
					variant: 'danger',
					icon: 'exclamation-triangle',
					text: `${t('Daily publish limit reached')} (${publicationLimit.limit})`,
				});
				return;
			}
			requestPublish({
				productID: productToPublish.id,
				merchantIntegrationID: mi.id,
				integrationKey: mi.integrationKey,
			});
			showStatus({
				variant: 'primary',
				icon: 'box-arrow-up-right',
				text: t('Opening the marketplace to publish the item...'),
			});
			return;
		}

		if (mi.metadata?.channel === 'website') {
			// Publish via the in-app form.
			redirect(`products/${productToPublish.id}/publish/${mi.id}`);
			return;
		}
	};

	const markPublished = async (mi: MerchantIntegration) => {
		if (productToPublish == null) {
			return;
		}
		setPendingMerchantIntegrationID(mi.id);
		try {
			await api.addPublication(mi.id, productToPublish.id);
		} catch (err) {
			setPendingMerchantIntegrationID(null);
			handleError(err as Error);
			return;
		}
		setPendingMerchantIntegrationID(null);
		updateProductPublications(mi);
	};

	const markUnpublished = async (mi: MerchantIntegration) => {
		if (productToPublish == null) {
			return;
		}
		setPendingMerchantIntegrationID(mi.id);
		try {
			await api.removePublication(mi.id, productToPublish.id);
		} catch (err) {
			setPendingMerchantIntegrationID(null);
			handleError(err as Error);
			return;
		}
		setPendingMerchantIntegrationID(null);
		onPublicationsChange(
			productToPublish.id,
			(productToPublish.publications ?? []).filter((p) => p.merchantIntegrationID !== mi.id),
		);
	};

	const togglePublished = (mi: MerchantIntegration, isPublished: boolean) => {
		if (isPublished) {
			markUnpublished(mi);
		} else {
			markPublished(mi);
		}
	};

	const hasIntegrations = merchantIntegrations.length > 0;
	const thumbnailURL =
		productToPublish != null
			? `${import.meta.env.VITE_PRODUCT_IMAGE_URL}/${productToPublish.id}/thumbnail-${productToPublish.image.fileName}.webp`
			: '';
	const productTitle = productToPublish?.texts[i18n.language as LocaleCode]?.title ?? '';

	return (
		<Dialog
			title={t('Publish')}
			className='publish-dialog'
			isOpen={product != null}
			onAfterHide={onClose}
			variant='neutral'
		>
			{isLoading ? (
				<Spinner />
			) : hasIntegrations ? (
				<>
					{productToPublish != null && (
						<div className='publish-dialog__header'>
							<img
								className='publish-dialog__product-image'
								src={thumbnailURL}
								alt={productTitle}
							/>
							<span className='publish-dialog__product-title'>{productTitle}</span>
						</div>
					)}
					<div className='publish-dialog__list'>
						{merchantIntegrations.map((mi) => {
							const publication =
								productToPublish?.publications?.find(
									(p) => p.merchantIntegrationID === mi.id,
								) ?? null;
							const isPublished = publication != null;
							const isPending = pendingMerchantIntegrationID === mi.id;
							const listingURL =
								publication?.externalID != null
									? buildListingURL(mi, publication.externalID)
									: null;
							return (
								<div className='publish-dialog__row' key={mi.id}>
									<SlCheckbox
										className='publish-dialog__check'
										checked={isPublished}
										disabled={isPending}
										onSlChange={() => togglePublished(mi, isPublished)}
									/>
									<img
										className='publish-dialog__logo'
										src={mi.metadata?.logoURL}
										alt={mi.metadata?.title}
									/>
									<span className='publish-dialog__title'>
										{mi.metadata?.title}
									</span>
									<div className='publish-dialog__action'>
										{isPublished ? (
											listingURL != null ? (
												<a
													className='publish-dialog__view'
													href={listingURL}
													target='_blank'
													rel='noreferrer'
												>
													{t('Edit')}
												</a>
											) : (
												<span className='publish-dialog__published'>
													{t('Published')}
												</span>
											)
										) : (
											<SlButton
												variant='default'
												size='small'
												loading={isPending}
												onClick={() => publish(mi)}
											>
												{t('Publish')}
											</SlButton>
										)}
									</div>
								</div>
							);
						})}
					</div>
				</>
			) : (
				<EmptyState
					icon='plug'
					description={t('Add an integration to republish your items on other channels')}
				>
					<SlButton
						variant='primary'
						onClick={() => {
							onClose();
							redirect('integrations');
						}}
					>
						{t('Add')}
					</SlButton>
				</EmptyState>
			)}
		</Dialog>
	);
};

export { PublishDialog };
